// CD: components/site-header/SiteHeader.jsx
// CD: wp/wordpress.css — QĐ#100 smart mobile bar; QĐ#105 language list; QĐ#167 mobile drawer slot.
import { EVENTS, defineOnce, fetchSectionsHtml, routesRoot, emit } from './pushe-ui.js';

export class PusheHeader extends HTMLElement {
  #off = [];
  #header;
  #bar;
  #drawer;
  #menu;
  #languages = [];
  #focusedLanguage = null;
  #desktop;
  #media;
  #pairs = [];
  #active;
  #lastY = 0;
  #frame;
  #held = false;
  #cartRequest;

  connectedCallback() {
    if (this.#off.length) return;
    this.#header = this.querySelector('.pushe-header');
    this.#bar = this.querySelector('.pushe-header__bar');
    if (!this.#header || !this.#bar) return;
    this.#drawer = this.querySelector('pushe-dialog');
    this.#menu = this.querySelector('.pushe-header__menu');
    if (!this.#menu || typeof this.#drawer?.open !== 'function' || typeof this.#drawer?.close !== 'function') return;
    this.#languages = Array.from(this.querySelectorAll('[data-pushe-lang]'));
    this.#media = window.matchMedia('(min-width: 900px)');
    this.#desktop = this.#media.matches;
    this.#lastY = window.pageYOffset;
    const listen = (target, type, fn, options) => {
      if (!target) return;
      target.addEventListener(type, fn, options);
      this.#off.push(() => target.removeEventListener(type, fn, options));
    };
    this.#pairs = Array.from(this.querySelectorAll('.pushe-header__nav .pushe-header__link')).flatMap((link, index) => {
      const panel = this.querySelector(`#pushe-mega-${index + 1}`);
      if (!panel) return [];
      link.setAttribute('aria-controls', panel.id);
      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');
      const pair = { link, panel };
      listen(link, 'pointerover', event => { if (event.pointerType !== 'touch') this.#openMega(pair); });
      listen(link, 'focusin', () => this.#openMega(pair));
      listen(link, 'click', event => {
        if (this.#media.matches && this.#active !== pair) { event.preventDefault(); this.#openMega(pair); }
      });
      return [pair];
    });
    this.#menu?.setAttribute('aria-controls', this.#drawer?.id ?? 'pushe-mobile-menu');
    this.#menu?.setAttribute('aria-expanded', 'false');
    listen(this.#menu, 'click', event => {
      if (!this.hasAttribute('data-pushe-header-ready')) return;
      event.preventDefault();
      if (this.#drawer?.isOpen) this.#drawer.close();
      else if (!this.#media.matches) { this.#closeMega(); this.#closeLanguage(); this.#drawer?.open(this.#menu); }
    });
    listen(this.#drawer, 'pushe:dialog:open', () => { this.#menu.setAttribute('aria-expanded', 'true'); this.#show(); });
    listen(this.#drawer, 'pushe:dialog:close', () => { this.#menu.setAttribute('aria-expanded', 'false'); this.#closeLanguage(); });
    listen(this.#header, 'pointerleave', () => {
      if (!this.#header.contains(this.ownerDocument.activeElement)) this.#closeMega();
    });
    listen(this, 'focusin', event => {
      this.#focusedLanguage = this.#languages.find(language => language.contains(event.target)) ?? null;
    });
    listen(this, 'focusout', event => {
      // CSS can hide the copy and blur it before matchMedia delivers its change event.
      if (this.#media.matches === this.#desktop) this.#focusedLanguage = null;
      if (this.#active && !this.#active.link.contains(event.relatedTarget) && !this.#active.panel.contains(event.relatedTarget)) this.#closeMega();
      if (!this.#languages.some(language => language.contains(event.relatedTarget))) this.#closeLanguage();
    });
    listen(this, 'keydown', event => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        if (this.#languages.some(language => language.open)) { event.preventDefault(); this.#closeLanguage(true); }
        else if (this.#active) { event.preventDefault(); this.#closeMega(true); }
      } else this.#megaKey(event);
    });
    listen(this.ownerDocument, 'pointerdown', event => {
      if (!this.#header.contains(event.target)) this.#closeMega();
      if (!this.#languages.some(language => language.contains(event.target))) this.#closeLanguage();
    });

    const updateReturnTo = form => {
      for (const input of form.querySelectorAll('input[name="return_to"]')) {
        input.value = window.location.pathname + window.location.search;
      }
    };
    for (const language of this.#languages) {
      listen(language, 'toggle', () => {
        if (language.open) { this.#show(); emit('pushe:layer:open', { source: 'language' }); }
      });
      listen(language.querySelector('form'), 'submit', event => updateReturnTo(event.currentTarget));
      listen(language, 'click', event => {
        const option = event.target.closest('[data-language-code]');
        if (!option) return;
        const form = option.closest('form');
        const code = form.querySelector('input[name="language_code"]');
        if (!code) return;
        event.preventDefault();
        code.value = option.dataset.languageCode;
        code.disabled = false;
        updateReturnTo(form);
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      });
    }
    listen(this.ownerDocument, 'pushe:layer:open', event => {
      if (event.detail?.source !== 'mega') this.#closeMega();
      if (event.detail?.source !== 'language') this.#closeLanguage();
    });
    listen(this.#media, 'change', () => {
      const focusedLanguage = this.#languages.find(language => language.contains(this.ownerDocument.activeElement)) ?? this.#focusedLanguage;
      const hiddenCopy = this.#media.matches ? this.#drawer : this.#bar;
      const restoreLanguage = Boolean(focusedLanguage && hiddenCopy?.contains(focusedLanguage));
      this.#desktop = this.#media.matches;
      let focusTarget;
      if (this.#media.matches) {
        const wasOpen = this.#drawer?.isOpen;
        this.#drawer?.close();
        if (restoreLanguage) focusTarget = this.#bar.querySelector('[data-pushe-lang] > summary');
        else if (wasOpen) focusTarget = this.querySelector('.pushe-header__brand');
      }
      else {
        this.#closeMega();
        if (restoreLanguage) focusTarget = this.#menu;
      }
      this.#closeLanguage();
      this.#show(); this.#lastY = window.pageYOffset;
      focusTarget?.focus();
    });
    listen(window, 'scroll', () => this.#queue(), { passive: true });
    listen(window, 'resize', () => this.#queue(), { passive: true });
    listen(this.#bar, 'focusin', () => this.#show());
    listen(this.ownerDocument, 'pushe:header-show', event => { this.#held = Boolean(event.detail?.hold); this.#show(); });
    listen(this.ownerDocument, 'pushe:header-release', () => { this.#held = false; this.#queue(); });
    listen(this.ownerDocument, EVENTS.CART_UPDATED, event => { this.#show(); this.#refreshCart(event.detail?.item_count); });
    // Adopt a native menu opened while this module was loading, before retiring its fallback CSS.
    const fallbackOpen = !this.#media.matches && window.location.hash === `#${this.#drawer.id}`;
    const fallbackCloseFocused = fallbackOpen && this.ownerDocument.activeElement?.classList.contains('pushe-drawer__close--fallback');
    if (fallbackOpen) {
      const { scrollX, scrollY } = window;
      this.#focusedLanguage = this.#languages.find(language => language.contains(this.ownerDocument.activeElement)) ?? null;
      this.#drawer.open(this.#menu, { adopt: true });
      if (this.ownerDocument.readyState !== 'complete') {
        // The pending native fragment navigation can still scroll when the page finishes loading.
        listen(window, 'load', () => {
          if (this.#drawer.isOpen) window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' });
        }, { once: true });
      }
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
    this.setAttribute('data-pushe-header-ready', '');
    if (fallbackCloseFocused) this.#drawer.querySelector('button.pushe-drawer__close')?.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    this.removeAttribute('data-pushe-header-ready');
    this.#off.splice(0).forEach(off => off());
    this.#cartRequest?.abort();
    if (this.#frame != null) window.cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.#drawer?.close();
    this.#closeMega();
    this.#closeLanguage();
    this.#held = false;
    this.#focusedLanguage = null;
    this.#show();
  }

  #show() { this.#header?.classList.remove('is-bar-hidden'); }
  #queue() {
    if (this.#frame != null) return;
    this.#frame = window.requestAnimationFrame(() => { this.#frame = null; this.#scroll(); });
  }
  #scroll() {
    const y = window.pageYOffset;
    const focused = this.#keyboardInBar();
    if (this.#media.matches || this.#held || this.#drawer?.isOpen || this.#languages.some(language => language.open) || focused) {
      this.#show(); this.#lastY = y; return;
    }
    const max = this.ownerDocument.documentElement.scrollHeight - window.innerHeight;
    if (y < 0 || y > max) return;
    if (this.#header.getBoundingClientRect().bottom > 0) { this.#show(); this.#lastY = y; return; }
    if (Math.abs(y - this.#lastY) < 8) return;
    this.#header.classList.toggle('is-bar-hidden', y > this.#lastY);
    this.#lastY = y;
  }

  #keyboardInBar() {
    const active = this.ownerDocument.activeElement;
    if (!active || active === this.ownerDocument.body || !this.#bar.contains(active)) return false;
    try { return active.matches(':focus-visible'); } catch { return true; }
  }

  #megaKey(event) {
    const pair = this.#active;
    if (!pair || event.altKey || event.ctrlKey || event.metaKey) return;
    const links = Array.from(pair.panel.querySelectorAll('a[href]'));
    if (!links.length) return;
    if (event.target === pair.link && !event.shiftKey && ['Tab', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      links[0].focus();
    } else if (event.key === 'Tab' && event.shiftKey && event.target === links[0]) {
      event.preventDefault();
      pair.link.focus();
    } else if (event.key === 'Tab' && !event.shiftKey && event.target === links.at(-1)) {
      // Panels follow the search row in the DOM; resume the parent navigation's
      // order explicitly instead of tabbing into an unrelated panel below it.
      const parents = Array.from(this.querySelectorAll('.pushe-header__nav .pushe-header__link'));
      const next = parents[parents.indexOf(pair.link) + 1] ?? this.querySelector('.pushe-header__brand');
      this.#closeMega();
      if (next) { event.preventDefault(); next.focus(); }
    }
  }

  #openMega(pair) {
    if (!this.#media.matches || this.#active === pair) return;
    this.#closeMega();
    this.#active = pair;
    pair.panel.removeAttribute('hidden');
    pair.link.setAttribute('aria-expanded', 'true');
    emit('pushe:layer:open', { source: 'mega' });
  }
  #closeMega(focus = false) {
    const pair = this.#active;
    if (!pair) return;
    // Focus while still active so focusin cannot reopen the panel being dismissed.
    if (focus) pair.link.focus();
    pair.panel.setAttribute('hidden', '');
    pair.link.setAttribute('aria-expanded', 'false');
    this.#active = null;
  }
  #closeLanguage(focus = false) {
    for (const language of this.#languages) {
      if (!language.open) continue;
      language.open = false;
      if (focus) language.querySelector('summary')?.focus();
    }
  }

  async #refreshCart(itemCount) {
    this.#cartRequest?.abort();
    this.#cartRequest = null;
    const currentBag = this.querySelector('a.pushe-header__cart');
    const shownCount = Number(currentBag?.querySelector('.pushe-header__count')?.textContent ?? 0);
    if (currentBag && Number.isInteger(itemCount) && itemCount >= 0 && shownCount === itemCount) return;
    const request = new AbortController();
    this.#cartRequest = request;
    // Section groups have generated ids on Shopify; use the rendered section.id, with the brief's static id as fallback.
    const id = this.dataset.sectionId || 'pushe-header';
    try {
      const sections = await fetchSectionsHtml([id], routesRoot(), { signal: request.signal });
      if (request.signal.aborted || !this.isConnected || !sections[id]) return;
      const Parser = window.DOMParser;
      const bag = new Parser().parseFromString(sections[id], 'text/html').querySelector('a.pushe-header__cart');
      const oldBag = this.querySelector('a.pushe-header__cart');
      if (bag && oldBag) {
        const focused = oldBag === this.ownerDocument.activeElement;
        oldBag.replaceWith(bag);
        if (focused) bag.focus();
      }
    } catch {
      // Keep the last server-rendered bag if Section Rendering is unavailable; the cart link remains usable.
    } finally {
      if (this.#cartRequest === request) this.#cartRequest = null;
    }
  }
}

if (typeof customElements !== 'undefined') defineOnce('pushe-header', PusheHeader);
